import axios from "axios";
import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import ReactPaginate from "react-paginate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import XlsxPopulate from "xlsx-populate/browser/xlsx-populate";

const BuyerLead = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [monthFilter, setMonthFilter] = useState("");
  const monthQuery = monthFilter ? `&month=${monthFilter}` : "";
  const request = localStorage.getItem("request");

  const fileInputRef = useRef(null);

  const [buyerId, setBuyerId] = useState(() => {
    return localStorage.getItem("buyer_id");
  });

  const handleRowClick = (id) => {
    const newId = buyerId === id ? null : id;
    setBuyerId(newId);

    if (newId) {
      localStorage.removeItem("buyer_id");
      localStorage.removeItem("request");
    } else {
      localStorage.removeItem("buyer_id");
    }
  };

  const highlightedUserIds =
    typeof location.state?.user_id === "string"
      ? location.state.user_id.split(",").map((id) => id.trim())
      : [];

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userType = user?.user_type;

  const isAlertShown = useRef(false);

  useEffect(() => {
    if (location.state && !isAlertShown.current) {
      toast.success(location.state);
      isAlertShown.current = true;
      window.history.replaceState({}, null);
    }
  }, [location.state]);

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState(0);
  const [usersId, setMupliUserId] = useState([]);

  // presentation-only state (density + name sort)
  const [dense, setDense] = useState(false);
  const [sortDir, setSortDir] = useState("");

  const BuyerLeadData = () => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", 200);

    if (search) params.append("search", search);
    if (monthFilter) params.append("month", monthFilter);

    axios
      .get(
        `${
          process.env.REACT_APP_URL
        }/admin/offline_buyer/all?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      )
      .then((res) => {
        setTotalPage(res?.data?.data?.pagination?.totalPages || 1);

        const orders = res?.data?.data?.orders || [];

        // ✅ Group by user_id
        const grouped = orders.reduce((acc, order) => {
          const uid = order.user?.user_id;
          if (!uid) return acc;

          if (!acc[uid]) {
            acc[uid] = {
              user: order.user,
              orders: [],
              latest_created_at: order.created_at,
            };
          }

          acc[uid].orders.push(order);

          // Update latest_created_at if this order is newer
          if (
            !acc[uid].latest_created_at ||
            new Date(order.created_at) > new Date(acc[uid].latest_created_at)
          ) {
            acc[uid].latest_created_at = order.created_at;
          }

          return acc;
        }, {});

        // Convert to array
        let groupedArray = Object.values(grouped);

        // ✅ Sort latest first
        groupedArray.sort(
          (a, b) =>
            new Date(b.latest_created_at) - new Date(a.latest_created_at)
        );

        // ✅ Subadmin filter: only show groups where this subadmin handled at least one order
        if (userType === "subadmin") {
          const subadminfiltereddata = groupedArray.filter((group) =>
            group.orders.some(
              (order) => order.sales_executive_id === user.user_id
            )
          );
          setData(subadminfiltereddata);
        } else {
          setData(groupedArray);
        }
      })
      .catch((err) => {
        if (err?.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        } else {
          console.error(
            "Buyer Lead fetch failed:",
            err.response?.data || err.message
          );
        }
      });
  };

  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleCheckboxChange = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const exportToExcel = async () => {
    try {
      if (!data || data.length === 0) {
        alert("No data available to export.");
        return;
      }

      // 🔹 Flatten and map data
      const exportData = data.map((item, index) => {
        const user = item.user || {};
        const latestOrder = item.orders?.[0] || {};
        return {
          "Sr. No.": index + 1,
          Name: user.name || "",
          Email: user.email || "",
          Phone: user.phone || "",
          Company: user.company_name || "",
          "Total Orders": item.orders?.length || 0,
          "Latest Product": latestOrder.title || "",
          "Latest Quantity": latestOrder.quantity || "",
          "Order Status": latestOrder.order_status || "",
          "Created At": new Date(latestOrder.created_at).toLocaleDateString(
            "en-IN",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }
          ),
        };
      });

      if (exportData.length === 0) {
        alert("No valid records found.");
        return;
      }

      // 🔹 Create worksheet & headers
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Buyer Leads");

      // 🔹 Column widths
      worksheet["!cols"] = Object.keys(exportData[0]).map((key) => ({
        wch: Math.max(15, key.length + 2),
      }));

      // 🔹 Basic formatting (using XlsxPopulate)
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const xpop = await XlsxPopulate.fromDataAsync(wbout);
      const sheet = xpop.sheet("Buyer Leads");

      // Header row style
      const headerRow = sheet.range(
        "A1:" + sheet.usedRange().endCell().columnName() + "1"
      );
      headerRow.style({
        bold: true,
        horizontalAlignment: "center",
        verticalAlignment: "center",
        fill: "ADD8E6", // Light Blue
        border: true,
      });

      // Data row styles (alternate coloring)
      const totalRows = sheet.usedRange().endCell().rowNumber();
      for (let i = 2; i <= totalRows; i++) {
        const row = sheet.range(
          `A${i}:${sheet.usedRange().endCell().columnName()}${i}`
        );
        row.style({
          border: true,
          horizontalAlignment: "left",
          verticalAlignment: "center",
        });
        if (i % 2 === 0) row.style({ fill: "F7F7F7" }); // light gray
      }

      // 🔹 Export final file
      const blob = await xpop.outputAsync();
      saveAs(
        new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "buyer-leads.xlsx"
      );

      toast.success("Excel exported successfully!");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export data.");
    }
  };

  const handleDownloadFormat = async () => {
    try {
      const headers = [
        "name",
        "email",
        "phone",
        "user_type",
        "company_name",
        "website",
        "gst_number",
        "business_type",
        "designation",
        "address",
        "city",
        "state",
        "country",
        "pincode",
        "product_id",
        "title",
        "quantity",
        "size",
        "material_color",
        "print_coverage",
        "product_description",
        "remark",
      ];

      const sampleRow = [
        "John Doe",
        "john@example.com",
        "9876543210",
        "buyer",
        "ABC Packaging",
        "https://abcpack.com",
        "27AAAPL1234C1ZV",
        "Retail",
        "Manager",
        "21 MG Road",
        "Mumbai",
        "Maharashtra",
        "India",
        "400001",
        "101",
        "Corrugated Box",
        "100",
        "10x10x5",
        "Brown",
        "Full",
        "Premium quality corrugated packaging box",
        "Sample remark",
      ];

      const wsData = [headers, sampleRow];

      // 🔹 Create workbook + worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "BuyerOrders");

      // 🔹 Column widths
      worksheet["!cols"] = headers.map((h) => ({
        wch: Math.max(15, h.length + 2),
      }));

      // 🔹 Export file
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, "buyer-order-import-format.xlsx");
      toast.success("Format downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to generate format file.");
    }
  };

  const handleImportClick = () => {
    document.getElementById("fileInput")?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!rows.length) {
          toast.error("Excel file is empty");
          return;
        }

        toast.loading("Importing, please wait...");

        const apiUrl = process.env.REACT_APP_URL;
        const token = localStorage.getItem("Token");

        for (const row of rows) {
          try {
            const registerPayload = {
              name: row.name || "",
              email: row.email || "",
              phone: row.phone || "",
              user_type: "offline_buyer",
              company_name: row.company_name || "",
              website: row.website || "",
              gst_number: row.gst_number || "",
              business_type: row.business_type || "",
              designation: row.designation || "",
              address: row.address || "",
              city: row.city || "",
              state: row.state || "",
              country: row.country || "",
              pincode: row.pincode || "",
              status: "active",
            };

            // Prepare order data
            const additionalFields = [
              { label: "Material Color", value: row.material_color },
              { label: "Print Coverage", value: row.print_coverage },
            ].filter((f) => f.value);

            const orderPayload = {
              product_id: row.product_id || "",
              title: row.title || "",
              quantity: row.quantity || "1",
              product_description: row.product_description || "",
              size: row.size || "",
              material_color: row.material_color || "",
              print_coverage: row.print_coverage || "",
              remark: row.remark || "",
              order_type: "offline_buyer", // ✅ fixed type for admin import
              additional_fields: additionalFields,
              user_data: registerPayload,
            };

            // Send to admin import controller
            const res = await axios.post(
              `${apiUrl}/admin/orders/import`,
              orderPayload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (res?.data?.success) {
            } else {
              console.warn(
                `⚠️ Order import issue for ${row.name}:`,
                res?.data?.message
              );
            }
          } catch (err) {
            console.error(
              `❌ Failed to import row for ${row.name}:`,
              err.response?.data || err.message
            );
            continue;
          }
        }

        toast.dismiss();
        toast.success("All records imported successfully!");
        BuyerLeadData();
      } catch (err) {
        toast.dismiss();
        console.error("Import failed:", err);
        toast.error("Failed to process Excel file");
      } finally {
        // ensure next click works even with the same file
        if (fileInputRef.current) fileInputRef.current.value = null;
      }
    };

    reader.readAsBinaryString(file);
  };

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  const handleWhatsAppClick = (phoneNumber) => {
    window.open(`https://wa.me/${phoneNumber}`, "_blank");
  };

  const handleCallClick = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  useEffect(() => {
    BuyerLeadData();
  }, [page, search, monthFilter, request, selectedUsers]);

  // Avatar initials + tint (presentation)
  const initialsOf = (name = "") =>
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";
  const AV_TINTS = [
    { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
    { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
    { bg: "var(--color-success-bg)", color: "var(--color-success-text)" },
    { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
    { bg: "var(--color-primary-weak)", color: "var(--color-primary)" },
  ];
  const avTint = (id = 0) =>
    AV_TINTS[Math.abs(Number(id) || String(id).length) % AV_TINTS.length];

  const toggleNameSort = () =>
    setSortDir((d) => (d === "asc" ? "desc" : d === "desc" ? "" : "asc"));

  const displayData = useMemo(() => {
    const rows = [...(data || [])];
    if (sortDir) {
      rows.sort((a, b) => {
        const av = (a?.user?.name || "").toLowerCase();
        const bv = (b?.user?.name || "").toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [data, sortDir]);

  return (
    <div className="bl">
      <Toaster position="top-center" reverseOrder={false} />

      {/* hidden import file input (preserved) */}
      <input
        type="file"
        id="fileInput"
        accept=".csv,.xlsx,.xls"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleFileUpload}
      />

      {/* page header */}
      <div className="bl-head">
        <div>
          <h1 className="bl-title">Buyer Lead</h1>
          <p className="bl-sub">
            Buyer enquiries and their packaging orders across the marketplace.
          </p>
        </div>
        <div className="bl-head__actions">
          {userType === "admin" && (
            <>
              <button type="button" className="bl-btn" onClick={exportToExcel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export
              </button>
              <button type="button" className="bl-btn" onClick={handleDownloadFormat}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <polyline points="9 15 12 18 15 15" />
                </svg>
                Download Format
              </button>
              <button type="button" className="bl-btn" onClick={handleImportClick}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Import
              </button>
            </>
          )}
          <button
            type="button"
            className="bl-btn bl-btn--primary"
            onClick={() =>
              Navigate("/admin/buyer-fake/add", { state: { usertype: "buyer" } })
            }
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Buyer Lead
          </button>
        </div>
      </div>

      {/* table card */}
      <div className="bl-card">
        {/* toolbar */}
        <div className="bl-toolbar">
          <div className="bl-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Search buyers by name, email, mobile…"
              value={search}
              onKeyDown={(e) =>
                e.key === "Enter" && search !== "" && BuyerLeadData()
              }
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="bl-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <select
              value={monthFilter}
              onChange={(e) => {
                setMonthFilter(e.target.value);
                BuyerLeadData();
              }}
            >
              <option value="">Select Month</option>
              <option value="All">All</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>
          <button
            type="button"
            className="bl-tool"
            style={{ marginLeft: "auto" }}
            onClick={() => setDense((d) => !d)}
            title="Toggle density"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {/* grid table */}
        <div className="bl-tablewrap">
          <div className="bl-table" style={{ "--bl-row-pad": dense ? "8px" : "13px" }}>
            <div className="bl-thead">
              <span className="bl-th bl-th--sort" onClick={toggleNameSort}>
                Name
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: sortDir ? 1 : 0.45, transform: sortDir === "desc" ? "rotate(180deg)" : "none", transition: "transform .15s ease" }}>
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </span>
              <span className="bl-th">Email</span>
              <span className="bl-th">Mobile Number</span>
              <span className="bl-th">Profile</span>
              <span className="bl-th">Orders</span>
              <span className="bl-th" style={{ textAlign: "right", justifyContent: "flex-end" }}>Action</span>
            </div>

            {displayData?.length !== 0 ? (
              displayData.map((item) => {
                const uid = item?.user?.user_id;
                const t = avTint(uid);
                const orderCount = item?.orders?.length || 0;
                const highlighted = highlightedUserIds.includes(String(uid));
                const selected =
                  selectedUsers.includes(uid) || buyerId === String(uid);
                return (
                  <div
                    className={`bl-row ${selected ? "is-selected" : ""}`}
                    key={uid}
                    onClick={() => handleRowClick(uid)}
                  >
                    <div className="bl-id">
                      <span className="bl-avatar" style={{ background: t.bg, color: t.color }}>
                        {initialsOf(item?.user?.name)}
                      </span>
                      <span className="bl-name">{item?.user?.name || "—"}</span>
                    </div>
                    <span className="bl-muted bl-ellipsis">{item?.user?.email || "—"}</span>
                    <span className="bl-mono">{item?.user?.phone || "—"}</span>
                    <div>
                      {/*
                        The <img> src read `logo_img`, a field that does not
                        exist (the column is `logo_image`), so a user WITH a logo
                        still rendered a broken image. And a user without one got
                        a grey picture-placeholder that opened an empty preview —
                        a button that does nothing. Show the profile picture when
                        there is one and make it openable; otherwise fall back to
                        the same initials avatar used in the name column, which
                        is not clickable because there is nothing to open.
                      */}
                      {(() => {
                        const src =
                          item?.user?.profile_image || item?.user?.logo_image;

                        if (!src) {
                          return (
                            <span
                              className="bl-avatar"
                              style={{ background: t.bg, color: t.color }}
                              title={item?.user?.name || "No profile image"}
                            >
                              {initialsOf(item?.user?.name)}
                            </span>
                          );
                        }

                        return (
                          <button
                            type="button"
                            className="bl-logo"
                            title="View profile image"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImage(
                                `${process.env.REACT_APP_IMAGE_URL}${src}`
                              );
                              setShowImagesModal(true);
                            }}
                          >
                            <img
                              src={`${process.env.REACT_APP_IMAGE_URL}${src}`}
                              alt={item?.user?.name || "Profile"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </button>
                        );
                      })()}
                    </div>
                    <div>
                      <button
                        type="button"
                        className={`bl-orders ${orderCount > 0 ? "has" : "none"} ${highlighted ? "hl" : ""}`}
                        title="View all orders for this buyer"
                        onClick={(e) => {
                          e.stopPropagation();
                          Navigate(`/admin/buyer/product-list/${uid}`);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                          <line x1="3" y1="6" x2="21" y2="6" />
                          <path d="M16 10a4 4 0 0 1-8 0" />
                        </svg>
                        <span>
                          {orderCount > 0
                            ? `View ${orderCount} ${orderCount === 1 ? "order" : "orders"}`
                            : "No orders"}
                        </span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </div>
                    <div className="bl-actions">
                      <button
                        type="button"
                        className="bl-act bl-act--call"
                        title="Call buyer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCallClick(item.user?.phone);
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.1 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72 12 12 0 0 0 .56 2.57 2 2 0 0 1-.45 2.11L9 11a16 16 0 0 0 5 5l1.6-1.1a2 2 0 0 1 2.1-.45 12 12 0 0 0 2.57.56A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="bl-act bl-act--wa"
                        title="WhatsApp buyer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWhatsAppClick(item.user?.phone);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 448 512" fill="currentColor">
                          <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bl-empty">
                <div className="bl-empty__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <p className="bl-empty__title">No buyer leads found</p>
                <p className="bl-empty__text">Try adjusting your search or filters.</p>
              </div>
            )}
          </div>
        </div>

        {/* pagination */}
        <div className="bl-foot">
          <span className="bl-foot__info">
            Showing <strong>{displayData.length}</strong> buyer leads
          </span>
          {data?.length !== 0 && (
            <ReactPaginate
              previousLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              }
              nextLabel={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              }
              breakLabel={"..."}
              pageCount={totalpage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              activeClassName={"active"}
            />
          )}
        </div>
      </div>

      {/* image preview modal (preserved) */}
      {showImagesModal && (
        <div className="bl-modal" onClick={() => setShowImagesModal(false)}>
          <div className="bl-modal__box" onClick={(e) => e.stopPropagation()}>
            {selectedImage ? (
              <img src={selectedImage} alt="Preview" className="bl-modal__img" />
            ) : (
              <p className="bl-muted">No image available</p>
            )}
            <button type="button" className="bl-btn bl-btn--primary" onClick={() => setShowImagesModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      <style>{`
.bl{ color:var(--color-text-primary); }
.bl *{ box-sizing:border-box; }
.bl-mono{ font-family:var(--font-mono); font-size:12.5px; color:var(--color-text-secondary); }
.bl-muted{ color:var(--color-text-secondary); font-size:12.5px; }
.bl-ellipsis{ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.bl-head{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:18px; }
.bl-title{ margin:0; font-size:24px; font-weight:800; letter-spacing:-.025em; }
.bl-sub{ margin:5px 0 0; font-size:13.5px; color:var(--color-text-secondary); }
.bl-head__actions{ display:flex; align-items:center; gap:9px; flex-wrap:wrap; }
.bl-btn{ display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 14px; border-radius:10px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-primary); font-family:inherit; font-size:12.5px; font-weight:700; cursor:pointer; transition:background .15s ease; }
.bl-btn:hover{ background:var(--color-hover); }
.bl-btn--primary{ border:0; color:#fff; background:linear-gradient(135deg,var(--color-primary),var(--color-primary-2)); box-shadow:0 2px 8px rgba(79,70,229,.3); }
.bl-btn--primary:hover{ filter:brightness(1.06); }

.bl-card{ background:var(--color-surface); border:1px solid var(--color-border); border-radius:14px; box-shadow:var(--shadow-card); overflow:hidden; }
.bl-toolbar{ display:flex; align-items:center; gap:10px; padding:13px 16px; border-bottom:1px solid var(--color-border-soft); flex-wrap:wrap; }
.bl-search{ display:flex; align-items:center; gap:9px; height:36px; padding:0 11px; background:var(--color-surface-2); border:1px solid var(--color-border); border-radius:9px; flex:1; min-width:200px; max-width:320px; }
.bl-search input{ border:0; outline:0; background:transparent; font-family:inherit; font-size:12.5px; color:var(--color-text-primary); width:100%; }
.bl-select{ display:flex; align-items:center; gap:7px; height:36px; padding:0 11px; border-radius:9px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-secondary); }
.bl-select select{ border:0; outline:0; background:transparent; font-family:inherit; font-size:12.5px; font-weight:600; color:var(--color-text-secondary); cursor:pointer; }
.bl-tool{ display:flex; align-items:center; gap:7px; height:36px; padding:0 12px; border-radius:9px; border:1px solid var(--color-border); background:var(--color-surface); color:var(--color-text-secondary); font-family:inherit; font-size:12.5px; font-weight:600; cursor:pointer; }
.bl-tool:hover{ background:var(--color-hover); }

.bl-tablewrap{ overflow-x:auto; }
.bl-table{ min-width:920px; }
.bl-thead, .bl-row{ display:grid; grid-template-columns:1.35fr 1.6fr 1fr 64px 1.2fr 96px; gap:12px; align-items:center; }
.bl-thead{ padding:11px 18px; background:var(--color-surface-2); border-bottom:1px solid var(--color-border); position:sticky; top:0; z-index:1; }
.bl-th{ font-size:10.5px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:var(--color-text-tertiary); display:flex; align-items:center; gap:5px; }
.bl-th--sort{ cursor:pointer; user-select:none; }
.bl-row{ padding:var(--bl-row-pad,13px) 18px; border-bottom:1px solid var(--color-border-soft); transition:background .12s ease; cursor:pointer; }
.bl-row:last-child{ border-bottom:none; }
.bl-row:hover{ background:var(--color-hover); }
.bl-row.is-selected{ background:var(--color-primary-weak); }
.bl-id{ display:flex; align-items:center; gap:11px; min-width:0; }
.bl-avatar{ width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:11.5px; font-weight:700; flex:0 0 32px; }
.bl-name{ font-size:13px; font-weight:700; color:var(--color-text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.bl-logo{ width:34px; height:34px; border-radius:8px; background:var(--color-surface-2); border:1px solid var(--color-border); display:flex; align-items:center; justify-content:center; color:var(--color-text-tertiary); cursor:pointer; padding:0; overflow:hidden; }
.bl-logo:hover{ border-color:var(--color-primary); }
.bl-orders{ display:inline-flex; align-items:center; gap:8px; height:32px; padding:0 9px 0 10px; border-radius:9px; font-size:12px; font-weight:700; cursor:pointer; justify-self:start; transition:box-shadow .12s ease, transform .12s ease; border:1px solid transparent; }
.bl-orders:hover{ box-shadow:var(--shadow-card); transform:translateY(-1px); }
.bl-orders.has{ background:var(--color-primary-weak); color:var(--color-primary); }
.bl-orders.none{ background:var(--color-surface-2); color:var(--color-text-tertiary); border-color:var(--color-border); }
.bl-orders.hl{ box-shadow:0 0 0 2px var(--color-primary); }
.bl-actions{ display:flex; align-items:center; justify-content:flex-end; gap:6px; }
.bl-act{ width:32px; height:32px; border-radius:9px; border:1px solid var(--color-border); background:var(--color-surface); display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .15s ease; }
.bl-act--call{ color:var(--color-info-text); }
.bl-act--call:hover{ background:var(--color-info-bg); border-color:var(--color-info-text); }
.bl-act--wa{ color:#25D366; }
.bl-act--wa:hover{ background:var(--color-success-bg); border-color:#25D366; }

.bl-empty{ display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:48px 20px; text-align:center; }
.bl-empty__icon{ width:54px; height:54px; border-radius:50%; background:var(--color-surface-2); color:var(--color-text-tertiary); display:flex; align-items:center; justify-content:center; }
.bl-empty__title{ margin:0; font-size:15px; font-weight:700; color:var(--color-text-primary); }
.bl-empty__text{ margin:0; font-size:13px; color:var(--color-text-secondary); }

.bl-foot{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 18px; border-top:1px solid var(--color-border); flex-wrap:wrap; }
.bl-foot__info{ font-size:12px; color:var(--color-text-secondary); }
.bl-foot__info strong{ color:var(--color-text-primary); }
.bl-foot .pagination{ margin:0; }

.bl-modal{ position:fixed; inset:0; background:rgba(10,14,23,.6); display:flex; align-items:center; justify-content:center; z-index:9999; }
.bl-modal__box{ background:var(--color-surface); border:1px solid var(--color-border); padding:20px; border-radius:14px; max-width:90%; display:flex; flex-direction:column; align-items:center; gap:16px; box-shadow:var(--shadow-card-hover); }
.bl-modal__img{ width:180px; height:180px; object-fit:cover; border-radius:12px; box-shadow:var(--shadow-card); }
      `}</style>
    </div>
  );
};

export default BuyerLead;
